import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { QtplanLogo } from '../components/QtplanLogo';

export const METHODIK = [
  { slug: 'discovery', de: 'Discovery', en: 'Discovery', deSub: 'Vom Board zum Projektplan', enSub: 'From Board to Project Plan' },
  { slug: 'pert', de: 'PERT-Schätzungen', en: 'PERT Estimates', deSub: 'Risiko wird eine Zahl', enSub: 'Risk Becomes a Number' },
  { slug: 'squad-forming', de: 'Squad-Forming', en: 'Squad Forming', deSub: 'Team-Zusammensetzung als Methode', enSub: 'Team Composition as Method' },
  { slug: 'adrs', de: 'ADRs', en: 'ADRs', deSub: 'Architekturentscheidungen dokumentieren', enSub: 'Document Architecture Decisions' },
  { slug: 'resources', de: 'Ressourcen & Kosten', en: 'Resources & Costs', deSub: 'Infrastrukturkosten im Plan', enSub: 'Infrastructure Costs in the Plan' },
] as const;

export const LOESUNGEN = [
  { slug: 'projektmanager', de: 'Projektmanager', en: 'Project Manager', deSub: 'Stakeholder-Kommunikation & Konflikte', enSub: 'Stakeholder Communication & Conflicts' },
  { slug: 'engineering-manager', de: 'Engineering Manager', en: 'Engineering Manager', deSub: 'Squad-Forming & Skill-Gaps', enSub: 'Squad Forming & Skill Gaps' },
  { slug: 'engineer', de: 'Engineer', en: 'Engineer', deSub: 'Individuelle Schätzungen & Sichtbarkeit', enSub: 'Individual Estimates & Visibility' },
  { slug: 'freelancer', de: 'Freelancer', en: 'Freelancer', deSub: 'Professionelle Planung ohne Enterprise-Kosten', enSub: 'Professional Planning Without Enterprise Costs' },
  { slug: 'platform-engineer', de: 'Platform Engineer', en: 'Platform Engineer', deSub: 'Infrastrukturkosten & Architekturentscheidungen', enSub: 'Infrastructure Costs & Architecture Decisions' },
] as const;

export const navText = {
  de: { features: 'Features', methodik: 'Methodik', loesungen: 'Lösungen', playground: 'Playground', createCanvas: 'Canvas erstellen', createBoard: 'Board erstellen' },
  en: { features: 'Features', methodik: 'Methodology', loesungen: 'Solutions', playground: 'Playground', createCanvas: 'Create Canvas', createBoard: 'Create Board' },
} as const;

export function LandingTopNav({
  onScrollToFeatures,
  onScrollToDemo,
  onCreateBoard,
  onCreateCanvas,
  hidden,
}: {
  onScrollToFeatures: () => void;
  onScrollToDemo: () => void;
  onCreateBoard: () => void;
  onCreateCanvas: () => void;
  hidden?: boolean;
}) {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
  const t = navText[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.nav
      className={`landing-topnav ${scrolled ? 'landing-topnav--scrolled' : ''}`}
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: hidden ? 0 : 1, y: hidden ? -48 : 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ pointerEvents: hidden ? 'none' : 'auto' }}
    >
      <div className="landing-topnav-left">
        <div className="landing-topnav-brand">
          <QtplanLogo size={18} className="text-white" />
          <span className="landing-topnav-wordmark">qtplan</span>
        </div>
      </div>

      <div className="landing-topnav-center">
        <button className="landing-topnav-link" onClick={onScrollToFeatures}>
          {t.features}
        </button>

        <div className="landing-topnav-dropdown">
          <button className="landing-topnav-link landing-topnav-link--dropdown">
            {t.methodik}
            <ChevronDown size={12} />
          </button>
          <div className="landing-topnav-dropdown-menu">
            <div className="landing-topnav-dropdown-panel">
              {METHODIK.map((item) => (
                <Link key={item.slug} to={`/${lang}/features/${item.slug}`} className="landing-topnav-dropdown-item">
                  <span className="landing-topnav-dropdown-title">{lang === 'de' ? item.de : item.en}</span>
                  <span className="landing-topnav-dropdown-desc">{lang === 'de' ? item.deSub : item.enSub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="landing-topnav-dropdown">
          <button className="landing-topnav-link landing-topnav-link--dropdown">
            {t.loesungen}
            <ChevronDown size={12} />
          </button>
          <div className="landing-topnav-dropdown-menu">
            <div className="landing-topnav-dropdown-panel">
              {LOESUNGEN.map((item) => (
                <Link key={item.slug} to={`/${lang}/for/${item.slug}`} className="landing-topnav-dropdown-item">
                  <span className="landing-topnav-dropdown-title">{lang === 'de' ? item.de : item.en}</span>
                  <span className="landing-topnav-dropdown-desc">{lang === 'de' ? item.deSub : item.enSub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <button className="landing-topnav-link" onClick={onScrollToDemo}>
          {t.playground}
        </button>

        <Link className="landing-topnav-link" to={`/${lang}/pricing`}>
          No Pricing
        </Link>

        <Link className="landing-topnav-link" to={`/${lang}/legal/impressum`}>
          {lang === 'de' ? 'Impressum & AGB' : 'Legal & Terms'}
        </Link>
      </div>

      <div className="landing-topnav-right">
        <button className="landing-topnav-cta" onClick={onCreateCanvas}>
          {t.createCanvas}
        </button>
        <button className="landing-topnav-cta" onClick={onCreateBoard}>
          {t.createBoard}
        </button>
        <button
          className="landing-topnav-link"
          onClick={() => i18n.changeLanguage(lang === 'de' ? 'en' : 'de')}
        >
          {lang === 'de' ? 'EN' : 'DE'}
        </button>
      </div>

      {/* Mobile hamburger — direct child of nav, outside right section */}
      <button
        className="landing-topnav-mobile-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="landing-topnav-mobile-menu">
          <button className="landing-topnav-mobile-link" onClick={() => { onScrollToFeatures(); setMobileMenuOpen(false); }}>
            {t.features}
          </button>

          <div className="landing-topnav-mobile-section">{t.methodik}</div>
          {METHODIK.map((item) => (
            <Link key={item.slug} to={`/${lang}/features/${item.slug}`} className="landing-topnav-mobile-link landing-topnav-mobile-link--sub" onClick={() => setMobileMenuOpen(false)}>
              {lang === 'de' ? item.de : item.en}
            </Link>
          ))}

          <div className="landing-topnav-mobile-section">{t.loesungen}</div>
          {LOESUNGEN.map((item) => (
            <Link key={item.slug} to={`/${lang}/for/${item.slug}`} className="landing-topnav-mobile-link landing-topnav-mobile-link--sub" onClick={() => setMobileMenuOpen(false)}>
              {lang === 'de' ? item.de : item.en}
            </Link>
          ))}

          <button className="landing-topnav-mobile-link" onClick={() => { onScrollToDemo(); setMobileMenuOpen(false); }}>
            {t.playground}
          </button>

          <Link className="landing-topnav-mobile-link" to={`/${lang}/pricing`} onClick={() => setMobileMenuOpen(false)}>
            No Pricing
          </Link>

          <Link className="landing-topnav-mobile-link" to={`/${lang}/legal/impressum`} onClick={() => setMobileMenuOpen(false)}>
            {lang === 'de' ? 'Impressum & AGB' : 'Legal & Terms'}
          </Link>

          <div style={{ borderTop: '1px solid hsla(0,0%,100%,0.08)', margin: '8px 0' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 16px' }}>
            <button
              onClick={() => i18n.changeLanguage('de')}
              className={`landing-topnav-lang-option ${lang === 'de' ? 'landing-topnav-lang-option--active' : 'landing-topnav-lang-option--inactive'}`}
            >
              DE
            </button>
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`landing-topnav-lang-option ${lang === 'en' ? 'landing-topnav-lang-option--active' : 'landing-topnav-lang-option--inactive'}`}
            >
              EN
            </button>
          </div>
        </div>
      )}
    </motion.nav>
  );
}
